import { Component, Input, Optional } from '@angular/core';
import { ClassNames, Color, ITableColumn, Size } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqBaseColumnComponent, LsqDataTableComponent } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { PopoverPlacement } from 'ngx-smart-popover';
import { Observable } from 'rxjs';
import { EXPENSES_DESCRIPTION } from '../../../constants/copy.constants';
import {
    ExpenseFeeBreakdown,
    GetAccountOverviewByDateResponse
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';

@Component({
    selector: 'lsq-expenses-popover-column',
    template: `
        <ng-container [cdkColumnDef]="column.key">
            <th
                cdk-header-cell
                *cdkHeaderCellDef
                [ngClass]="[classNames.AlignRight, 'expenses-col']"
                [class.sortable]="column.sortable"
                (click)="changeSort()"
            >
                <div class="th-content">
                    <ng-container *ngIf="column.headerPopoverContent">
                        <popover-content
                            #headerPopover
                            [size]="column.headerPopoverSize || 'small'"
                        >
                            {{ column.headerPopoverContent }}
                        </popover-content>
                        <div
                            [popover]="headerPopover"
                            [popoverPlacement]="getHeaderPopoverPlacement()"
                        >
                            {{ column.header }}
                        </div>
                    </ng-container>

                    <ng-container *ngIf="!column.headerPopoverContent">
                        {{ column.header }}
                    </ng-container>

                    <div *ngIf="isSortable$ | async">
                        <lsq-icon
                            class="sort-caret"
                            [ngClass]="{ 'is-sorted': isCurrentlySorted() }"
                            [type]="getIconType()"
                            [color]="getIconColor()"
                        >
                        </lsq-icon>
                    </div>
                </div>
            </th>
            <td
                cdk-cell
                *cdkCellDef="let row; let index = index"
                [ngStyle]="{ width: column.width || 'auto' }"
                [ngClass]="[classNames.AlignRight, 'expenses-col', 'popover-pad']"
                [class.sortable]="column.sortable"
            >
                <span>{{ row.expenses | currencyDash }}</span>
                <popover-content #expensePopover [size]="size.MediumSmall">
                    <table class="expense-popover">
                        <tbody>
                            <tr *ngFor="let fee of row.expenseBreakdown.expensesList">
                                <td>{{ fee.category }}</td>
                                <td>
                                    <div>
                                        <lsq-icon
                                            [type]="icons.Add"
                                            [color]="color.Black"
                                        ></lsq-icon>
                                        {{ fee.amount | currencyDash }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td i18n="Expenses popover label">Expenses</td>
                                <td>{{ row.expenseBreakdown.totalExpenses | currencyDash }}</td>
                            </tr>
                        </tbody>
                    </table>
                </popover-content>
                <lsq-icon
                    *ngIf="displayPopover(row.expenseBreakdown)"
                    [type]="icons.Info"
                    [color]="color.Gray"
                    [ngClass]="[classNames.TableCellPopoverIcon, classNames.PopoverIcon]"
                    [popover]="expensePopover"
                    [popoverPlacement]="placement.BottomLeft"
                >
                </lsq-icon>
            </td>
            <td
                cdk-footer-cell
                *cdkFooterCellDef
                [ngClass]="[classNames.Sortable, classNames.AlignRight, 'expenses-col']"
            >
                <ng-container *ngIf="(data$ | async)?.totalExpenseBreakdown">
                    <span>{{ (data$ | async)?.totalExpenses | currencyDash }}</span>
                    <popover-content #totalExpensesPopover [size]="size.Medium">
                        <table class="expense-popover">
                            <tbody>
                                <tr
                                    *ngFor="
                                        let fee of (data$ | async)?.totalExpenseBreakdown
                                            .expensesList
                                    "
                                >
                                    <td>{{ fee.category }}</td>
                                    <td>
                                        <div>
                                            <lsq-icon
                                                [type]="icons.Add"
                                                [color]="color.Black"
                                            ></lsq-icon>
                                            {{ fee.amount | currencyDash }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td i18n="Expenses popover label">Expenses</td>
                                    <td>{{ (data$ | async)?.totalExpenses | currencyDash }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </popover-content>
                    <lsq-icon
                        *ngIf="
                            (data$ | async)?.totalExpenses > 0 &&
                            (data$ | async)?.totalExpenseBreakdown?.expensesList?.length > 0
                        "
                        [type]="icons.Info"
                        [color]="color.Gray"
                        [ngClass]="[classNames.TableCellPopoverIcon, classNames.PopoverIcon]"
                        [popover]="totalExpensesPopover"
                        [popoverPlacement]="placement.TopRight"
                    >
                    </lsq-icon>
                </ng-container>
            </td>
        </ng-container>
    `
})
export class ExpensesPopoverColumnComponent extends LsqBaseColumnComponent {
    @Input() public data$: Observable<GetAccountOverviewByDateResponse>;
    @Input() public column: ITableColumn = {
        key: 'expenses',
        header: '',
        headerPopoverContent: EXPENSES_DESCRIPTION,
        sortable: true
    };

    constructor(@Optional() public dataTable: LsqDataTableComponent, public i18n: I18n) {
        super(dataTable);
        if (!this.column.header) {
            this.column.header = this.i18n({
                value: 'Expenses',
                meaning: 'Expenses header label'
            });
        }
    }

    public classNames = ClassNames;
    public placement = PopoverPlacement;
    public icons = Icons;
    public color = Color;
    public size = Size;

    public displayPopover(expenseBreakdown: ExpenseFeeBreakdown): boolean {
        return (
            expenseBreakdown.totalExpenses > 0 &&
            expenseBreakdown.expensesList &&
            expenseBreakdown.expensesList.length > 0
        );
    }
}
